import React from 'react'
import { Box, Typography } from '@mui/material'
import {
  InsertDriveFile as DocumentIcon,
  PhotoCamera as CameraIcon,
  Videocam as VideoIcon,
  Headphones as AudioIcon
} from '@mui/icons-material'

interface QuoteBubbleProps {
  contextMessageId: string
  allMessages?: any[]
  onScrollToQuote?: (id: string) => void
  isOutbound: boolean
  leadName?: string
  smbName?: string
}

export const QuoteBubble: React.FC<QuoteBubbleProps> = ({
  contextMessageId,
  allMessages,
  onScrollToQuote,
  isOutbound,
  leadName,
  smbName
}) => {
  if (!contextMessageId || !allMessages) return null
  const originalMsg = allMessages.find((m) => m?.id === contextMessageId)

  if (!originalMsg) {
    return (
      <Box
        sx={{
          borderLeft: `4px solid ${isOutbound ? '#fff' : 'grey.400'}`,
          bgcolor: isOutbound ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          p: 1,
          mb: 1,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontStyle: 'italic',
            fontSize: '0.75rem',
            color: isOutbound ? 'rgba(255,255,255,0.7)' : 'text.secondary',
          }}
        >
          Original message not available
        </Typography>
      </Box>
    )
  }

  const isMediaMsg = ['image', 'video', 'audio', 'document'].includes(originalMsg.message_type)
  let previewText = originalMsg.content
  let mediaIcon: React.ReactNode = null
  let thumbnailUrl: string | null = null

  if (isMediaMsg) {
    thumbnailUrl = ['image', 'video'].includes(originalMsg.message_type)
      ? (originalMsg.msg_media_metadata?.media_url || null)
      : null
    switch (originalMsg.message_type) {
      case 'image':
        previewText = originalMsg.content || 'Photo'
        mediaIcon = <CameraIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
        break
      case 'video':
        previewText = originalMsg.content || 'Video'
        mediaIcon = <VideoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
        break
      case 'audio':
        previewText = originalMsg.content || 'Audio'
        mediaIcon = <AudioIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
        break
      case 'document':
        previewText =
          originalMsg.msg_media_metadata?.media_filename ||
          originalMsg.msg_media_metadata?.file_name ||
          originalMsg.content ||
          'Document'
        mediaIcon = <DocumentIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
        break
    }
  } else if (originalMsg.message_type === 'interactive') {
    const origInteractiveMetadata = originalMsg.msg_interactive_metadata || {}
    let origInteractive = origInteractiveMetadata.interactive_data || {}
    if (typeof origInteractive === 'string') {
      try {
        origInteractive = JSON.parse(origInteractive)
      } catch (e) {
        origInteractive = {}
      }
    }
    const origHeader = origInteractive.header
    const origBody = typeof origInteractive.body === 'object' ? origInteractive.body?.text : origInteractive.body
    const buttonReplyText = origInteractive.button_reply?.title || origInteractive.title
    const interactiveBody = buttonReplyText || origBody || origInteractive.text || originalMsg.content || 'Interactive Message'

    if (origHeader?.type === 'image') {
      thumbnailUrl = origHeader.link || null
      mediaIcon = <CameraIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
      previewText = interactiveBody
    } else if (origHeader?.type === 'video') {
      const containingMsg = allMessages.find(m => m.msg_context_metadata?.context_message_id === contextMessageId)
      const isButtonReply = containingMsg?.msg_interactive_metadata?.interactive_type === 'button_reply'
      if (!isButtonReply) {
        thumbnailUrl = origHeader.link || null
      }
      mediaIcon = <VideoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
      previewText = interactiveBody
    } else if (origHeader?.type === 'document') {
      mediaIcon = <DocumentIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
      previewText = interactiveBody
    } else if (origHeader?.type === 'text') {
      const headerText = origHeader.text
      previewText = headerText ? `${headerText}: ${interactiveBody}` : interactiveBody
    } else {
      previewText = interactiveBody
    }
  } else if (originalMsg.message_type === 'template') {
    const templateData = originalMsg.msg_template_metadata || {}
    const components = templateData.platform_template_components || []
    const headerComponent = components.find((c: any) => c.type === 'HEADER')
    const headerFormat = headerComponent?.format?.toUpperCase()
    const mediaUrl = originalMsg.msg_media_metadata?.media_url || headerComponent?.example?.header_handle?.[0]

    previewText = originalMsg.content || 'Template Message'

    if (headerFormat === 'IMAGE') {
      thumbnailUrl = mediaUrl || null
      mediaIcon = <CameraIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
    } else if (headerFormat === 'VIDEO') {
      thumbnailUrl = mediaUrl || null
      mediaIcon = <VideoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
    } else if (headerFormat === 'DOCUMENT') {
      mediaIcon = <DocumentIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
    }
  }

  if (!previewText) {
    previewText = 'Message'
  }

  // Filter out invalid/empty/broken URLs for thumbnail
  const finalThumbnail = (thumbnailUrl && thumbnailUrl.startsWith('http')) ? thumbnailUrl : null

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation()
        if (onScrollToQuote) onScrollToQuote(contextMessageId)
      }}
      sx={{
        borderLeft: `4px solid ${isOutbound ? '#fff' : 'grey.400'}`,
        bgcolor: isOutbound ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        mb: 0.5,
        borderRadius: '8px 8px 4px 4px',
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex',
        width: '100%',
        transition: 'background-color 0.2s ease',
        '&:hover': {
          bgcolor: isOutbound ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'
        }
      }}
    >
      <Box sx={{ p: 1, flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isOutbound ? '#fff' : 'text.secondary',
            display: 'block',
            mb: 0.25
          }}
        >
          {originalMsg.direction === 'outbound'
            ? smbName || 'You'
            : leadName || 'Lead'}
        </Typography>
        <Box
          sx={{
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: isOutbound ? '#fff' : 'text.primary',
            opacity: 0.9
          }}
        >
          {mediaIcon}
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              color: 'inherit'
            }}
          >
            {previewText}
          </Typography>
        </Box>
      </Box>
      {finalThumbnail && (
        <Box
          sx={{
            width: 60,
            height: 60,
            flexShrink: 0,
            m: 0.5,
            borderRadius: 0.5,
            overflow: 'hidden'
          }}
        >
          <img
            src={finalThumbnail}
            alt="Preview"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      )}
    </Box>
  )
}
